import { createSeededRandom } from "../random/seeded-random";
import type {
  EngineContext,
  GamePhase,
  GameStateV1,
  LaneId,
  LaneState,
  PlayerId,
  PlayerLaneState,
  PlayerState,
} from "../types";
import { createEmptyResourcePool, createEmptyTurnActionUsage } from "./state-helpers";

export const DEMO_ENGINE_CONTEXT: EngineContext = {
  rulesVersion: "first-edition",
  cardDataVersion: "demo-v1",
};

export interface DemoGameStateOptions {
  readonly seed?: string;
  readonly matchId?: string;
  readonly activePlayerId?: PlayerId;
  readonly phase?: Exclude<GamePhase, "finished">;
  readonly rulesVersion?: string;
  readonly cardDataVersion?: string;
}

interface DemoSideConfig {
  readonly control: number;
  readonly maximumControl: number;
  readonly commanderAttack: number;
  readonly commanderGuard: number;
  readonly armyAttack: number;
  readonly armyGuard: number;
}

function createDemoSide(
  playerId: PlayerId,
  laneId: LaneId,
  config: DemoSideConfig,
): PlayerLaneState {
  const prefix = `demo:${playerId}:${laneId}`;

  return {
    status: "active",
    land: {
      cardInstanceIds: [`${prefix}:land`],
      stage: 1,
      currentControl: config.control,
      maximumControl: config.maximumControl,
      attackBonus: 0,
      guardBonus: 0,
    },
    commander: {
      cardInstanceId: `${prefix}:commander`,
      attack: config.commanderAttack,
      guard: config.commanderGuard,
      armyCapacity: 3,
      readiness: "ready",
      deployedThisTurn: false,
      mayAttackOnDeploymentTurn: false,
    },
    armies: [
      {
        cardInstanceId: `${prefix}:army`,
        armySize: 1,
        attack: config.armyAttack,
        guard: config.armyGuard,
      },
    ],
    conspiracyCardInstanceId: null,
    attachedCardInstanceIds: [],
    combatModifiers: [],
  };
}

function createLane(
  laneId: LaneId,
  playerOne: DemoSideConfig,
  playerTwo: DemoSideConfig,
): LaneState {
  return {
    laneId,
    result: "active",
    players: {
      "player-1": createDemoSide("player-1", laneId, playerOne),
      "player-2": createDemoSide("player-2", laneId, playerTwo),
    },
  };
}

function createPlayer(playerId: PlayerId): PlayerState {
  return {
    playerId,
    deck: [],
    hand: [],
    discard: [],
    exhaust: [],
    eventResolution: [],
    activePolicyId: null,
    victoryBanners: 0,
    actionsUsed: createEmptyTurnActionUsage(),
    kingdomPool: createEmptyResourcePool(),
  };
}

/**
 * A deterministic Battle-Phase state for UI wiring and the first combat slice.
 * Player 1 can demonstrate a blocked attack on the right or claim the left and
 * center lanes to finish the match.
 */
export function createDemoGameState(options: DemoGameStateOptions = {}): GameStateV1 {
  const seed = options.seed ?? "fate-kingdom-demo-seed";
  const rulesVersion = options.rulesVersion ?? DEMO_ENGINE_CONTEXT.rulesVersion;
  const cardDataVersion = options.cardDataVersion ?? DEMO_ENGINE_CONTEXT.cardDataVersion;

  return {
    schemaVersion: 1,
    rulesVersion,
    cardDataVersion,
    matchId: options.matchId ?? `demo-match:${seed}`,
    seed,
    random: createSeededRandom(seed),
    turnNumber: 1,
    activePlayerId: options.activePlayerId ?? "player-1",
    phase: options.phase ?? "battle",
    players: {
      "player-1": createPlayer("player-1"),
      "player-2": createPlayer("player-2"),
    },
    lanes: {
      left: createLane(
        "left",
        {
          control: 10,
          maximumControl: 10,
          commanderAttack: 4,
          commanderGuard: 1,
          armyAttack: 2,
          armyGuard: 1,
        },
        {
          control: 3,
          maximumControl: 10,
          commanderAttack: 2,
          commanderGuard: 2,
          armyAttack: 1,
          armyGuard: 1,
        },
      ),
      center: createLane(
        "center",
        {
          control: 10,
          maximumControl: 10,
          commanderAttack: 4,
          commanderGuard: 2,
          armyAttack: 1,
          armyGuard: 1,
        },
        {
          control: 2,
          maximumControl: 10,
          commanderAttack: 2,
          commanderGuard: 2,
          armyAttack: 1,
          armyGuard: 1,
        },
      ),
      right: createLane(
        "right",
        {
          control: 10,
          maximumControl: 10,
          commanderAttack: 2,
          commanderGuard: 2,
          armyAttack: 0,
          armyGuard: 1,
        },
        {
          control: 10,
          maximumControl: 10,
          commanderAttack: 3,
          commanderGuard: 3,
          armyAttack: 2,
          armyGuard: 2,
        },
      ),
    },
    actionLog: [],
    eventQueue: [],
    status: { type: "active" },
  };
}
